import { useIntl } from "react-intl"
import { useDispatch } from "react-redux"
import { useState } from "react"
import { Combobox, EInputTypes, ETypeOfNotification, InputDate, InputTextCustom, getDate, setNotification, useFormValidation } from "medere-ui-framework"
import { BudgetDTO } from "../../dto/budget/BudgetDTO"
import { ComboDataDTO } from "../../dto/ComboDataDTO"
import { Button } from "primereact/button"
import './Budget.scss'
import BudgetTable from "./tables/BudgetTable"
import { HealthEntityPlanDTO } from "../../dto/HealthEntityPlanDTO"
import { generateBenefitOrderFromBudget, getBillingEntities, getModalities } from "../../servicesHooks/Budget/useBudgetService"
import { HttpStatusCode } from "axios"
import CancelPreinvoiceModal from "./CancelPreinvoiceModal"
import HealthPatientCoverageModal from "../Patient/HealthPatientCoverageModal"
import NoHealthPatientCoverageModal from "./NoHealthPatientCoverageModal"
import './Budget.scss'
import { useEntityService } from "../../servicesHooks/Entity/useEntityService"

/**
 * Campos obligatorios al generar la orden: los del formulario que se marcan en rojo. Se valida solo esto porque el
 * resto del DTO (surgery, budgetnumber…) puede venir en null del backend, y eso bloqueaba sin marcar nada.
 */
const REQUIRED_FIELDS: (keyof BudgetDTO)[] = ["date", "billingentity", "attentionmodality", "duration", "agreement", "agreementplan"];

interface PreinvoiceFromBudgetViewProps {
    budget:BudgetDTO;
    hideView: () => void;
    setSelectedAction: (action:number) => void;
}

export default function PreinvoiceFromBudgetView({budget, hideView, setSelectedAction}:PreinvoiceFromBudgetViewProps) {

    const { getHealthEntityComboDataDTO, getHealthEntityPlanDTO } = useEntityService();
    const intl = useIntl();
    const dispatch = useDispatch();
    const [budgetToPreInvoice, setBudgetToPreInvoice] = useState<BudgetDTO>({
        ...budget,
        date:new Date(),
        patientdesc: budget.addressee ?? budget.patientdesc ?? ''
    });
    const [generalEarnings, setGeneralEarnings] = useState<number>(0); 
    const {errors: formErrors, setErrors: setFormErrors, handleValidation} = useFormValidation(budgetToPreInvoice);

    const [openCancelBudgetModal, setOpenCancelBudgetModal] = useState<boolean>(false); 
    const [openNoHealthPatientCoverageModal, setOpenNoHealthPatientCoverageModal] = useState<boolean>(false); 
    const [openHealthPatientCoverageModal, setOpenHealthPatientCoverageModal] = useState<boolean>(false); 

      
    function cancel (value:boolean) { 
        setOpenCancelBudgetModal(false);
        if(value)
            hideView();
    }
    
    function generateOrder (budgetToPreInvoice: BudgetDTO, withValidation:boolean) {
        if(withValidation) {
            const ignoredFields = (Object.keys(budgetToPreInvoice) as (keyof BudgetDTO)[]).filter(field => !REQUIRED_FIELDS.includes(field));
            // Se valida desde cero ({}): un error viejo de un campo que ya no se pide no tiene que bloquear.
            handleValidation(()=>{
                generateOrder(budgetToPreInvoice,false);
            }, {}, ignoredFields,
            () => {
                dispatch(setNotification({ title: "Error al guardar los datos", desc: "Por favor verifque todos los campos", icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }))
            });
            return;
        }
        generateBenefitOrderFromBudget(budgetToPreInvoice)?.then(res => {
            if (res.status == HttpStatusCode.Ok){
                dispatch(setNotification({ title: "Orden generada", icon: 'pi-check-circle', typeOfNotification: ETypeOfNotification.INFO }))
                hideView();
                return;
            }
        }).catch(err => {
            if(err.response.status == HttpStatusCode.PreconditionFailed) {
                setOpenNoHealthPatientCoverageModal(true);
                return;
            }
            dispatch(setNotification({ title: "Error al intentar guardar los datos", icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }))
        });
    }

    return (
        <div className='min-h-screen flex-column mb-5'>
            <Button label="< Volver" className="linkButton" onClick={() => setSelectedAction(-1)} />
            <div className="flex md:gap-4 gap-2 align-items-end flex-column md:flex-row">
                <h2 className="text-2xl mainTitle my-3">
                    Orden de prestación
                </h2>
            </div>
            
            <div className='flex flex-column md:flex-row justify-content-center md:space-x-4'>
                <InputDate label={"Fecha de orden"}
                    className="input-common md:w-3/12" 
                    value={getDate(budgetToPreInvoice.date)} 
                    error={formErrors.date}
                    onChange={(e: any) => 
                        setBudgetToPreInvoice({...budgetToPreInvoice, date: e.value})
                    }
                    required
                />
                <Combobox label={"Emisor"} 
                    className={`input-common md:w-4/12`} 
                    getItems={getBillingEntities}
                    allowsEmptyValues={false}
                    optionLabel='name' 
                    placeholder="Seleccionar"
                    value={budgetToPreInvoice.billingentitydesc}
                    setValue={(e:ComboDataDTO) => {
                        if (e) {
                            setBudgetToPreInvoice(prev => ({
                              ...prev,
                              billingentity: e.id ?? -1,
                              billingentitydesc: e.name ?? ''
                            }));
                          }
                    }} 
                    error={formErrors.billingentity}
                    required
                />
                <Combobox label={"Modalidad"} 
                    className={`input-common md:w-3/12`} 
                    getItems={getModalities}
                    allowsEmptyValues={false}
                    optionLabel='name' 
                    placeholder="Seleccionar"
                    value={budgetToPreInvoice.attentionmodalitydesc}
                    setValue={(e:ComboDataDTO) => {
                        if(e)
                            setBudgetToPreInvoice(prev => ({ ...prev, attentionmodality: e.id ?? -1, attentionmodalitydesc: e.name ?? ''}));
                    }} 
                    error={formErrors.attentionmodality}
                    required
                />
                <InputTextCustom label={intl.formatMessage({ id: "Duration" })}
                    className="input-common md:w-1/12" 
                    value={budgetToPreInvoice.duration}
                    inputType={EInputTypes.NUMBER}
                    error={formErrors.duration}
                    onChange={(e: any) => {
                        setBudgetToPreInvoice({ ...budgetToPreInvoice, duration: e.target.value });
                    }} 
                    required
                />
                <InputTextCustom label={"% Ganancia"}
                    labelClassName="text-nowrap"
                    className="input-common md:w-1/12 text-nowrap"
                    value={generalEarnings}
                    inputType={EInputTypes.NUMBER}
                    onChange={(e: any) => {
                        setGeneralEarnings(e.target.value);
                    }} 
                />
            </div>
            <div className='flex flex-column align-items-end md:flex-row justify-content-center md:space-x-4 mb-4'>
                <InputTextCustom label={intl.formatMessage({id:'Addressee'})}
                    className="input-common md:w-4/12"
                    value={budgetToPreInvoice.addressee ?? ''}
                    disable
                    inputType={EInputTypes.TEXT}
                    onChange={() => {}}
                    required
                />

                <InputTextCustom label={"DNI"}
                    className="input-common md:w-2/12"
                    value={budgetToPreInvoice.dni}
                    disable
                    inputType={EInputTypes.TEXT}
                    onChange={(e: any) => {}} 
                />

                <Combobox label={"Obra social"} 
                    className={`input-common md:${(budgetToPreInvoice.agreement !== -1 && budgetToPreInvoice.agreementdesc != "")?'w-3/12':'w-6/12'}`} 
                    getItems={getHealthEntityComboDataDTO}
                    optionLabel='name' 
                    placeholder="Seleccionar"
                    value={budgetToPreInvoice.agreementdesc}
                    setValue={(e:ComboDataDTO) => {
                        if(e) {
                            setBudgetToPreInvoice(
                                prev => (
                                    { ...prev, agreement: e.id, agreementdesc: e.name, agreementplan: -1, agreementplandesc: ''}
                                )
                            )
                        }
                    }} 
                    error={formErrors.agreement}
                    required
                />

                {(budgetToPreInvoice.agreement !== -1 && budgetToPreInvoice.agreementdesc != "") && <Combobox label={"Plan"} 
                    className="input-common md:w-3/12" 
                    getItems={getHealthEntityPlanDTO} 
                    params={{healthentity:budgetToPreInvoice.agreement}} 
                    optionLabel='name' 
                    value={budgetToPreInvoice.agreementplandesc}
                    setValue={(e:HealthEntityPlanDTO) => {
                        if(e){
                            setBudgetToPreInvoice(prev => ({ 
                                ...prev,
                                agreementplan: e.healthentityplan ?? -1, agreementplandesc: e.name
                            }));
                        }
                    }}
                    reLoadItemsValue={budgetToPreInvoice.agreement} 
                    error={formErrors.agreementplan}
                    allowsEmptyValues={false}
                />}

            </div>

            <BudgetTable budget={budgetToPreInvoice} setBudget={setBudgetToPreInvoice} defaultEarnings={generalEarnings} date={budgetToPreInvoice.date ?? undefined} selectedHealthEntity={budgetToPreInvoice.agreement ?? undefined} selectedHealthEntityPlan={budgetToPreInvoice.agreementplan ?? undefined}/>
            
            <div className="flex flex-row w-full mt-4 justify-content-end align-items-end">
                <Button
                className="buttonMain3" 
                label={intl.formatMessage({ id: "Cancel" })}
                onClick={() => setOpenCancelBudgetModal(true)}
                />

                <Button
                className="buttonMain"
                label="Generar órden"
                onClick={() => generateOrder(budgetToPreInvoice,true)}
                />
            </div>
            <CancelPreinvoiceModal openCancelBudgetModal={openCancelBudgetModal} setOpenCancelBudgetModal={setOpenCancelBudgetModal} cancelPreinvoice={cancel}></CancelPreinvoiceModal>
            <NoHealthPatientCoverageModal patientName={budgetToPreInvoice.patientdesc} healthEntity={budgetToPreInvoice.agreementdesc ?? ''} openNoHealthPatientCoverageModal={openNoHealthPatientCoverageModal} setOpenNoHealthPatientCoverageModal={setOpenNoHealthPatientCoverageModal} setOpenHealthPatientCoverageModal={setOpenHealthPatientCoverageModal}/>
            <HealthPatientCoverageModal 
                header={`Nueva cobertura para ${budgetToPreInvoice.patientdesc}`}
                defaultCoverage={{
                    healthpatientcoverage: -1,
                    coveragestart: new Date(),
                    coverageend: null,
                    patient: budgetToPreInvoice.patient,
                    patientdesc: budgetToPreInvoice.patientdesc,
                    healthentity: budgetToPreInvoice.agreement,
                    healthentitydesc: budgetToPreInvoice.agreementdesc,
                    healthentityplan: budgetToPreInvoice.agreementplan,
                    healthentityplandesc: budgetToPreInvoice.agreementplandesc,
                    affiliatenumber: (() => {
                        const match = budgetToPreInvoice.affiliatenumber?.match(/\d+/);
                        return match ? parseInt(match[0], 10) : null;
                    })(),
                    voluntary: false,
                    primarycoverage: false,
                }}
                openHealthPatientCoverageModal={openHealthPatientCoverageModal}
                setOpenHealthPatientCoverageModal={setOpenHealthPatientCoverageModal}           
            />
        </div>
    )
}
